import { Link } from "react-router";
import type { StudentDetail, StudentPayment } from "@edu/shared";
import { dayCountText, meetingCountText } from "../../lib/plurals.ts";
import { TRASH_KEEP_DAYS } from "../homework/trash-keep.ts";

import { agoLabel, nextLabel, type WhenLabel } from "../../lib/when.ts";
import {
  homeworkFact,
  homeworkFactText,
  monthlyQuiet,
  monthlySummary,
} from "../../lib/student-facts.ts";

/**
 * Сводка по ученику: четыре факта и быстрые действия.
 *
 * Стоит первой и в окне, и на странице, потому что отвечает на те вопросы, с
 * которыми к ученику приходят каждый день. До перестройки 18.08.2026 первой
 * панелью карточки был «Доступ», куда заходят раз в жизни, а занятия и домашка
 * лежали под ним: карточка была отсортирована по важности решений, а не по
 * частоте вопросов.
 *
 * ⚠️ Плитки печатаются ВСЕ ЧЕТЫРЕ всегда, в том числе словами про отсутствие
 * («не запланирован», «не задавали», «нет ответов»). Это то же правило, по
 * которому построены списки кабинета: исчезающая плитка двигает соседние, и
 * карточки двух учеников перестают сравниваться взглядом.
 *
 * ⚠️ **У этого правила есть цена, и она однажды уже выстрелила.** Пустота под
 * ним выглядит как ответ: три плитки из четырёх были мертвы всю жизнь карточки
 * (ручка не клала в ответ три необязательных поля), и «не задавали» читалось не
 * как дыра, а как факт. Нашлось это только жалобой владельца 23.08.2026.
 *
 * Отсюда следствие, которое стоит помнить шире этого файла: **экран, у которого
 * пустота неотличима от содержимого, глазами не проверяется никогда и обязан
 * иметь тест на непустоту.** Он есть: `apps/api/src/student-card.dbtest.ts`.
 *
 * ⚠️ Плитки учёта (пятая и шестая) появляются и исчезают ЦЕЛИКОМ вместе с
 * переключателем человека, а не по наличию данных у ученика. Правило
 * сравнимости этим не нарушено: переключатель один на всех учеников, поэтому
 * либо плитки есть у всех карточек, либо их нет ни у одной.
 */
export function StudentBrief(props: {
  data: StudentDetail;
  now: number;
  tracking: { attendance: boolean; payments: boolean };
}) {
  const s = props.data.student;
  const { attempts, correct } = props.data.progress;
  const hw = homeworkFact(s.lastHomework, props.now);

  const next = nextLabel(s.nextLessonAt ?? null, props.now);
  const last = agoLabel(s.lastLessonAt ?? null, props.now, "занятий не было");

  return (
    /*
      ⚠️ Коробки у сводки НЕТ, и это решение, а не пропуск (14.09.2026). Плитка
      сама себе коробка, и карточка вокруг них дала бы четыре коробки внутри
      пятой. Плитки поэтому стоят блоком экрана, а действия под ними своим
      блоком: расстояние между ними задаёт колонка страницы.
    */
    <>
      {/* `<dl>`, а не сетка из `<div>`: это буквально список пар «имя и
          значение», и читалка называет подпись вместе со значением */}
      <dl className="tiles">
        <Fact label="Следующий урок" when={next} />
        <Fact label="Последнее занятие" when={last} />
        <Fact
          label="Домашка"
          value={homeworkFactText(hw)}
          // Значение тут ФРАЗА при любом состоянии («сдана», «в работе»,
          // «просрочено 23 дня»), поэтому обычная гарнитура, а не моноширинная
          words
          tone={hw.kind === "overdue" ? "danger" : hw.when.tone}
          title={hw.title ?? undefined}
          quiet={hw.kind === "none"}
        />
        {/*
          ⚠️ «Ответов пока нет» и «ноль верных» это разные сообщения, поэтому
          сервер отдаёт две величины, а не готовый процент, и делит их клиент.
          Считается по попыткам (`exercise_attempt`), то есть по тому, что
          ученик действительно нажимал, а не по вердикту на доске: вердикта в
          CRDT нет и не будет (правило 3 раздела 5.3 CLAUDE.md).
        */}
        <Fact
          label="Верных ответов"
          value={attempts === 0 ? "нет ответов" : `${Math.round((correct * 100) / attempts)}%`}
          note={attempts === 0 ? undefined : `${correct} из ${attempts} попыток`}
          quiet={attempts === 0}
        />

        {/*
          ⚠️ Тон у плитки оплат ВСЕГДА нейтральный, тревожного цвета здесь нет
          ни при каком долге. Решение владельца 21.08.2026, и оно про смысл, а
          не про палитру: преподаватель может знать об оплате и просто не
          отмечать, и алая цифра превратила бы его привычку в ошибку. Красным в
          карточке остаётся только просроченная домашка, у которой срок задали
          мы сами.
        */}
        {props.tracking.payments && props.data.payment !== undefined && (
          <Fact
            label="Оплата"
            value={paymentText(props.data.payment)}
            // Фраза при любом режиме: «не оплачен сентябрь», «всё отмечено»
            words
            quiet={isQuietPayment(props.data.payment)}
          />
        )}

        {props.tracking.attendance && props.data.payment !== undefined && (
          <Fact
            label="Пропусков"
            value={
              props.data.payment.absentCount === null || props.data.payment.absentCount === 0
                ? "нет"
                : String(props.data.payment.absentCount)
            }
            note={props.data.payment.absentCount ? "за полгода" : undefined}
            quiet={!props.data.payment.absentCount}
          />
        )}
      </dl>

      {/*
        ⚠️ «Задать домашнюю» отсюда уехала в шапку экрана 14.09.2026: это главное
        действие карточки, а место главного действия одно на весь продукт, в
        правом конце строки заголовка. Здесь остаётся переход к уже выданным
        работам, и он обычного веса, потому что это переход, а не действие.

        Целевой путь из проекта домашки (раздел 3) от переезда не изменился:
        карточка ученика, кнопка, выбор заготовки, срок предзаполнен, «Задать».

        Отвязанному новую работу не задать (сервер ответит 409
        `student_not_linked`), поэтому кнопки нет ни здесь, ни в шапке, а причина
        названа словами.
      */}
      {s.unlinked ? (
        <p className="muted">
          Ученик отвязан: новую домашку не задать, пока не отправите ему новую ссылку.
          Его домашки ушли в корзину и через {dayCountText(TRASH_KEEP_DAYS)} удалятся вместе с
          ответами; новая ссылка вернёт их.
        </p>
      ) : (
        <div className="acts">
          <Link className="btn" to={`/homework?studentId=${s.userId}`}>
            Домашки ученика
          </Link>
        </div>
      )}
    </>
  );
}

/**
 * Что написано в плитке оплат.
 *
 * Три разных ответа, и путать их нельзя. У помесячной пары единица оплаты это
 * МЕСЯЦ: «не оплачен сентябрь» либо «оплачен по ноябрь». У поурочной единица
 * занятие, и «ноль» это ответ, а не отсутствие данных. У пары с режимом `none`
 * учёт выключен по одной этой паре.
 *
 * ⚠️ Подпись плитки при этом одна на все три случая («Оплата»), а не своя у
 * каждого режима. Плитки карточки сравнивают взглядом между учениками, и
 * заголовок, меняющийся вместе со значением, ломает это сравнение: глаз ищет то
 * же место, а находит другое слово.
 */
function paymentText(payment: StudentPayment): string {
  if (payment.mode === "monthly") return monthlySummary(payment);
  if (payment.mode === "none") return "не веду";
  if (payment.unpaidCount === null) return "не веду";
  return payment.unpaidCount === 0 ? "всё отмечено" : `не отмечено ${meetingCountText(payment.unpaidCount)}`;
}

/** Гасим то, что про отсутствие данных, чтобы оно не спорило с настоящими. */
function isQuietPayment(payment: StudentPayment): boolean {
  if (payment.mode === "none") return true;
  // ⚠️ Спрашиваем СОСТОЯНИЕ, а не массив: проверка «ни одной отметки нет»
  // гасила и «не оплачен август», то есть самое обычное состояние новой пары
  if (payment.mode === "monthly") return monthlyQuiet(payment);
  return payment.unpaidCount === null || payment.unpaidCount === 0;
}

/**
 * Плитка факта.
 *
 * Либо ярлык времени (`when`), либо готовое значение (`value`): у первых трёх
 * плиток значение это время, и тон с подсказкой считает `lib/when.ts`, а
 * пересказывать их своими словами в каждой плитке значит потерять подсказку с
 * полной датой.
 */
function Fact(props: {
  label: string;
  /*
    Поля объявлены как «или undefined», а не через `?`: в проекте включён
    `exactOptionalPropertyTypes`, и с ним «свойства нет» и «свойство равно
    undefined» это разные типы. Вычисленное значение, которого может не быть,
    подходит только под второй.
  */
  when?: WhenLabel | undefined;
  value?: string | undefined;
  note?: string | undefined;
  tone?: "danger" | "accent" | "none" | undefined;
  title?: string | undefined;
  /** Значение про отсутствие данных: гасим, чтобы не спорило с настоящими. */
  quiet?: boolean | undefined;
  /**
   * Значение это ФРАЗА, а не число.
   *
   * ⚠️ Моноширинный стоит там, где числа сравнивают глазами, и внутри фразы его
   * не бывает никогда. Умолчание считается само, и считается верно: ярлык
   * времени (`when`) это «3 дня назад» и «не запланирован» ровно так же часто,
   * как «вт 16:30», а у готового значения фразой оказывается именно то, что про
   * отсутствие данных («нет ответов», «нет»). Обе плитки, где фраза стоит
   * ВСЕГДА, говорят об этом явно.
   */
  words?: boolean | undefined;
}) {
  const text = props.when?.text ?? props.value ?? "";
  const tone = props.tone ?? props.when?.tone ?? "none";
  const title = props.title ?? (props.when?.title || undefined);
  const quiet = props.quiet ?? props.when?.title === "";
  const words = props.words ?? (props.when !== undefined || quiet);

  return (
    <div className="tile">
      <dt className="tile__k">{props.label}</dt>
      <dd
        className={words ? "tile__v tile__v--words" : "tile__v"}
        data-tone={tone}
        data-quiet={quiet}
        title={title}
      >
        {text}
      </dd>
      {/* Пояснение ВТОРЫМ `<dd>`, а не узлом внутри значения: нескольких `<dd>`
          к одному `<dt>` разметка допускает, а прижать пояснение к низу плитки
          можно только пока оно её прямой ребёнок */}
      {props.note !== undefined && <dd className="tile__s">{props.note}</dd>}
    </div>
  );
}
